import { CopyButton } from "@/components/ui/CopyButton";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Text } from "@/components/ui/Text";
import { useNewRemoteSessionClientCallbackUrl } from "@gram/client/react-query/newRemoteSessionClientCallbackUrl.js";
import { CreateRemoteSessionClientFormTokenEndpointAuthMethod } from "@gram/client/models/components/createremotesessionclientform.js";
import {
  UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat,
  type UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat as AuthAudienceFormat,
} from "@gram/client/models/components/updateremotesessionclientform.js";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import {
  CLIENT_TYPE_LABELS,
  clientTypeHelp,
  isPrivateKeyJwtAuthMethod,
  type ClientType,
} from "./issuerFormUtils";

// RedirectURICallout shows the redirect_uri operators must register on the
// upstream provider's OAuth app before typed-in client credentials will
// work. Rendered inside ClientCredentialsFields so both the Attach sheet's
// Manual add path and the Modify sheet's existing-client edit path show it.
// An existing client passes the callbackURL it was registered with, or null
// when it registered none; a client not yet created passes undefined and
// shows the one the server will register for it.
function RedirectURICallout({
  callbackURL,
}: {
  callbackURL?: string | null;
}): JSX.Element | null {
  const newClientCallback = useNewRemoteSessionClientCallbackUrl(
    undefined,
    undefined,
    { enabled: callbackURL === undefined, throwOnError: false },
  );
  if (callbackURL === null) return null;
  const redirectURI = callbackURL ?? newClientCallback.data?.callbackUrl;
  if (redirectURI === undefined && newClientCallback.isError) {
    return (
      <Alert variant="error" dismissible={false}>
        Couldn't load the redirect URI to register.{" "}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void newClientCallback.refetch()}
        >
          <Button.Text>Retry</Button.Text>
        </Button>
      </Alert>
    );
  }
  if (redirectURI === undefined) {
    return (
      <Text muted small>
        Loading redirect URI…
      </Text>
    );
  }
  return (
    <Stack gap={2}>
      <Label className="text-muted-foreground text-xs">Redirect URI</Label>
      <div className="bg-muted/50 p-4 font-mono text-sm">
        <div className="flex items-center justify-between gap-2">
          <code className="break-all">{redirectURI}</code>
          <CopyButton
            size="xs"
            text={redirectURI}
            tooltip="Copy redirect URI"
          />
        </div>
      </div>
      <Text muted small>
        Register this as the callback / redirect URI on the upstream
        authorization server's OAuth app before pasting credentials below.
      </Text>
    </Stack>
  );
}

// Shared form-field components used by both AttachRemoteIdentityProviderSheet
// and ModifyRemoteIdentityProviderSheet. The state lives in the parent sheet;
// these are pure presentation components. Type aliases and small helpers
// live in issuerFormUtils.ts so this module exports components only (react
// fast refresh requirement).

// IssuerUrlField is the single Issuer URL input. Split out from the Endpoints
// section so callers can render a Slug field between them.
//
// `onIssuerUrlSettled` fires on blur, once the operator has stopped typing.
// Callers use it to run the duplicate preflight: these sheets make no automatic
// network calls while typing, and blur is what keeps that true.
//
// `duplicateWarning` is a slot rather than something this component fetches, so
// every surface renders the warning in the same place — directly under the
// field it is about — while each tier keeps its own scope and its own idea of
// what "use the existing one instead" means.
export function IssuerUrlField({
  issuerUrl,
  onIssuerUrlChange,
  onIssuerUrlSettled,
  duplicateWarning,
}: {
  issuerUrl: string;
  onIssuerUrlChange: (value: string) => void;
  onIssuerUrlSettled?: (value: string) => void;
  duplicateWarning?: JSX.Element | null;
}): JSX.Element {
  return (
    <Stack gap={2}>
      <Label className="text-muted-foreground text-xs">Issuer URL</Label>
      <Input
        value={issuerUrl}
        onChange={onIssuerUrlChange}
        onBlur={() => onIssuerUrlSettled?.(issuerUrl)}
        placeholder="https://login.example.com"
      />
      <Text muted small>
        Issuer URL of the upstream authorization server.
      </Text>
      {duplicateWarning}
    </Stack>
  );
}

// EndpointsFields renders the Endpoints section: Discover/Reset slot, the
// four RFC 8414 endpoint inputs, and any discovery warnings. Pure
// presentation — discovery state and handlers stay in the parent sheet.
export function EndpointsFields({
  issuerUrl,
  authorizationEndpoint,
  tokenEndpoint,
  registrationEndpoint,
  jwksUri,
  endpointWarnings,
  discoverPending,
  discoverError,
  showDiscoverControls,
  showResetControls,
  onAuthorizationEndpointChange,
  onTokenEndpointChange,
  onRegistrationEndpointChange,
  onJwksUriChange,
  onDiscover,
  onResetEndpoints,
}: {
  issuerUrl: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  registrationEndpoint: string;
  jwksUri: string;
  endpointWarnings: string[];
  discoverPending: boolean;
  discoverError: string | null;
  showDiscoverControls: boolean;
  showResetControls: boolean;
  onAuthorizationEndpointChange: (value: string) => void;
  onTokenEndpointChange: (value: string) => void;
  onRegistrationEndpointChange: (value: string) => void;
  onJwksUriChange: (value: string) => void;
  onDiscover: () => void;
  onResetEndpoints: () => void;
}): JSX.Element {
  return (
    <Stack gap={3} className="border-t pt-6">
      <Stack gap={1}>
        <Label className="text-sm font-medium">Endpoints</Label>
        {showDiscoverControls && (
          <Text muted small>
            Discover fetches the issuer's RFC 8414 metadata and fills these
            fields in. Edit anything that needs to be overridden.
          </Text>
        )}
        {showResetControls && (
          <Text muted small>
            Restore discovered values for modified endpoints.
          </Text>
        )}
      </Stack>

      {showDiscoverControls && (
        <div>
          <Button
            variant="secondary"
            disabled={!issuerUrl.trim() || discoverPending}
            onClick={onDiscover}
          >
            <Button.Text>
              {discoverPending ? "Discovering…" : "Discover"}
            </Button.Text>
          </Button>
        </div>
      )}

      {showResetControls && (
        <div>
          <Button variant="secondary" onClick={onResetEndpoints}>
            <Button.Text>Reset to Discovered Values</Button.Text>
          </Button>
        </div>
      )}

      {showDiscoverControls && discoverError && (
        <Alert variant="error" dismissible={false}>
          {discoverError}
        </Alert>
      )}

      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">
          Authorization Endpoint
        </Label>
        <Input
          value={authorizationEndpoint}
          onChange={onAuthorizationEndpointChange}
          placeholder="https://login.example.com/authorize"
        />
      </Stack>

      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">Token Endpoint</Label>
        <Input
          value={tokenEndpoint}
          onChange={onTokenEndpointChange}
          placeholder="https://login.example.com/token"
        />
      </Stack>

      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">
          Registration Endpoint (optional)
        </Label>
        <Input
          value={registrationEndpoint}
          onChange={onRegistrationEndpointChange}
          placeholder="https://login.example.com/register"
        />
        <Text muted small>
          Used for DCR. Enter it manually when issuer metadata does not
          advertise one.
        </Text>
      </Stack>

      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">
          JWKS URI (optional)
        </Label>
        <Input
          value={jwksUri}
          onChange={onJwksUriChange}
          placeholder="https://login.example.com/.well-known/jwks.json"
        />
      </Stack>

      {endpointWarnings.length > 0 && (
        <Alert variant="warning" dismissible={false}>
          <Stack gap={1}>
            {endpointWarnings.map((warning) => (
              <span key={warning}>{warning}</span>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  );
}

// TokenEndpointAuthMethodField is the standalone Select for the upstream
// token endpoint auth method. It renders for the DCR client type (so operators
// can override the upstream-assigned default) and inside the manual
// ClientCredentialsFields. Extracted so the two paths stay in lockstep on the
// enum values and placeholder copy.
export function TokenEndpointAuthMethodField({
  value,
  onChange,
  allowPrivateKeyJwt = false,
  privateKeyJwtUnavailable,
  hideNone = false,
  disabled = false,
}: {
  value: CreateRemoteSessionClientFormTokenEndpointAuthMethod | "";
  onChange: (
    value: CreateRemoteSessionClientFormTokenEndpointAuthMethod | "",
  ) => void;
  allowPrivateKeyJwt?: boolean;
  /**
   * Lists private_key_jwt even when it can't be picked, with this reason
   * beside it, so the option is visible rather than missing.
   */
  privateKeyJwtUnavailable?: string;
  /** A service account must prove who it is, so `none` is not offered. */
  hideNone?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Stack gap={2}>
      <Label className="text-muted-foreground text-xs">
        Token Endpoint Auth Method
      </Label>
      <Select
        value={value}
        onValueChange={(next) =>
          onChange(next as CreateRemoteSessionClientFormTokenEndpointAuthMethod)
        }
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue placeholder="client_secret_basic (default)" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            value={
              CreateRemoteSessionClientFormTokenEndpointAuthMethod.ClientSecretBasic
            }
          >
            client_secret_basic
          </SelectItem>
          <SelectItem
            value={
              CreateRemoteSessionClientFormTokenEndpointAuthMethod.ClientSecretPost
            }
          >
            client_secret_post
          </SelectItem>
          {!hideNone && (
            <SelectItem
              value={CreateRemoteSessionClientFormTokenEndpointAuthMethod.None}
            >
              none
            </SelectItem>
          )}
          {privateKeyJwtUnavailable !== undefined &&
            !allowPrivateKeyJwt &&
            value !==
              CreateRemoteSessionClientFormTokenEndpointAuthMethod.PrivateKeyJwt && (
              <SelectItem
                value={
                  CreateRemoteSessionClientFormTokenEndpointAuthMethod.PrivateKeyJwt
                }
                disabled
              >
                private_key_jwt · {privateKeyJwtUnavailable}
              </SelectItem>
            )}
          {(allowPrivateKeyJwt ||
            value ===
              CreateRemoteSessionClientFormTokenEndpointAuthMethod.PrivateKeyJwt) && (
            <SelectItem
              value={
                CreateRemoteSessionClientFormTokenEndpointAuthMethod.PrivateKeyJwt
              }
            >
              private_key_jwt
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </Stack>
  );
}

export function ClientAssertionAudienceField({
  value,
  onChange,
  disabled = false,
}: {
  value: AuthAudienceFormat;
  onChange: (value: AuthAudienceFormat) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <Stack gap={2}>
      <Label className="text-muted-foreground text-xs">
        Client assertion audience
      </Label>
      <Select
        value={value}
        onValueChange={(next) => onChange(next as AuthAudienceFormat)}
        disabled={disabled}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            value={
              UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat.Issuer
            }
          >
            Issuer URL (default)
          </SelectItem>
          <SelectItem
            value={
              UpdateRemoteSessionClientFormTokenEndpointAuthAudienceFormat.TokenEndpoint
            }
          >
            Token endpoint URL
          </SelectItem>
        </SelectContent>
      </Select>
      <Text small muted>
        Use the issuer URL unless your identity provider requires the token
        endpoint URL, as required by providers such as Okta.
      </Text>
    </Stack>
  );
}

// ClientCredentialsFields is used by both Add (editable client_id) and
// Modify (client_id read-only — the API has no rotate path; create a fresh
// remote_session_client if you need a new one). The secret input is hidden
// for private_key_jwt; empty means "leave existing in place" in Modify. Callers that
// render their own section header (e.g. above a client-type selector) pass
// showHeading={false} to suppress the built-in "OAuth Client Credentials" block.
export function ClientCredentialsFields({
  clientId,
  clientSecret,
  tokenEndpointAuthMethod,
  allowPrivateKeyJwt = false,
  clientIdEditable = true,
  clientSecretLabel = "Client Secret (optional)",
  clientSecretPlaceholder = "••••••••",
  showHeading = true,
  callbackURL,
  onClientIdChange,
  onClientSecretChange,
  onTokenEndpointAuthMethodChange,
}: {
  clientId: string;
  clientSecret: string;
  tokenEndpointAuthMethod:
    | CreateRemoteSessionClientFormTokenEndpointAuthMethod
    | "";
  allowPrivateKeyJwt?: boolean;
  clientIdEditable?: boolean;
  clientSecretLabel?: string;
  clientSecretPlaceholder?: string;
  showHeading?: boolean;
  // callbackURL is the redirect URI of an existing client, or null when it
  // registered none. Omit it for a client not yet created.
  callbackURL?: string | null;
  onClientIdChange: (value: string) => void;
  onClientSecretChange: (value: string) => void;
  onTokenEndpointAuthMethodChange: (
    value: CreateRemoteSessionClientFormTokenEndpointAuthMethod | "",
  ) => void;
}): JSX.Element {
  return (
    <Stack gap={4} className={showHeading ? "border-t pt-6" : undefined}>
      {showHeading && (
        <Stack gap={1}>
          <Label className="text-sm font-medium">
            OAuth Client Credentials
          </Label>
          <Text muted small>
            The platform acts as an OAuth client against the upstream issuer.
            Register a client with the issuer out-of-band and paste the
            credentials here.
          </Text>
        </Stack>
      )}

      <RedirectURICallout callbackURL={callbackURL} />

      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">Client ID</Label>
        {clientIdEditable ? (
          <Input
            value={clientId}
            onChange={onClientIdChange}
            placeholder="client_abc123"
          />
        ) : (
          <Text small mono className="break-all">
            {clientId || "—"}
          </Text>
        )}
      </Stack>

      {isPrivateKeyJwtAuthMethod(tokenEndpointAuthMethod) ? (
        <Text muted small>
          Any existing client secret is retained but not used with
          private_key_jwt.
        </Text>
      ) : (
        <Stack gap={2}>
          <Label className="text-muted-foreground text-xs">
            {clientSecretLabel}
          </Label>
          <Input
            type="password"
            value={clientSecret}
            onChange={onClientSecretChange}
            placeholder={clientSecretPlaceholder}
          />
        </Stack>
      )}

      <TokenEndpointAuthMethodField
        value={tokenEndpointAuthMethod}
        onChange={onTokenEndpointAuthMethodChange}
        allowPrivateKeyJwt={allowPrivateKeyJwt}
      />
    </Stack>
  );
}

// OverridesFields renders the per-client OAuth dance overrides. Both fields
// are optional and apply in both DCR and manual modes — they control what
// Speakeasy sends at authorize/token time, independent of how the client was
// registered.
export function OverridesFields({
  scopeOverride,
  audienceOverride,
  onScopeOverrideChange,
  onAudienceOverrideChange,
}: {
  scopeOverride: string;
  audienceOverride: string;
  onScopeOverrideChange: (value: string) => void;
  onAudienceOverrideChange: (value: string) => void;
}): JSX.Element {
  return (
    <Stack gap={4} className="border-t pt-6">
      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">
          Scope (override)
        </Label>
        <Input
          value={scopeOverride}
          onChange={onScopeOverrideChange}
          placeholder="read, write, openid"
        />
        <Text muted small>
          Comma-separated. When provided, every sign-in requests these scopes.
          Otherwise sign-ins request what the MCP server asks for, falling back
          to the issuer's scopes_supported.
        </Text>
      </Stack>

      <Stack gap={2}>
        <Label className="text-muted-foreground text-xs">
          Audience (optional)
        </Label>
        <Input
          value={audienceOverride}
          onChange={onAudienceOverrideChange}
          placeholder="https://api.example.com"
        />
        <Text muted small>
          When provided, the platform includes this audience in authorize and
          token requests (RFC 8707). Required by some providers (e.g. Auth0) to
          return JWT access tokens.
        </Text>
      </Stack>
    </Stack>
  );
}

// ClientTypeFields renders the Client Type selector (DCR / CIMD / Manual) and
// the credentials inputs the chosen type needs. Used by both the org-admin
// CreateRemoteSessionClientSheet and the Attach sheet so the two stay in
// lockstep on the available-types logic and per-type copy. The selector hides
// when only one type is available (nothing to choose). The owning sheet holds
// `clientType` so its submit handler can branch on it.
export function ClientTypeFields({
  availableTypes,
  clientType,
  onClientTypeChange,
  clientId,
  clientSecret,
  tokenEndpointAuthMethod,
  onClientIdChange,
  onClientSecretChange,
  onTokenEndpointAuthMethodChange,
}: {
  availableTypes: ClientType[];
  clientType: ClientType;
  onClientTypeChange: (value: ClientType) => void;
  clientId: string;
  clientSecret: string;
  tokenEndpointAuthMethod:
    | CreateRemoteSessionClientFormTokenEndpointAuthMethod
    | "";
  onClientIdChange: (value: string) => void;
  onClientSecretChange: (value: string) => void;
  onTokenEndpointAuthMethodChange: (
    value: CreateRemoteSessionClientFormTokenEndpointAuthMethod | "",
  ) => void;
}): JSX.Element {
  let credentials: JSX.Element | null;
  switch (clientType) {
    case "dcr":
      // DCR mints the client_id/client_secret at save time, so the only input
      // is the token endpoint auth method forwarded to the registration call.
      credentials = (
        <TokenEndpointAuthMethodField
          value={tokenEndpointAuthMethod}
          onChange={onTokenEndpointAuthMethodChange}
        />
      );
      break;
    case "cimd":
      // CIMD has no credentials to collect, and the selector help text above
      // already explains the hosted-document flow, so render nothing here.
      credentials = null;
      break;
    case "manual":
      credentials = (
        <ClientCredentialsFields
          showHeading={false}
          clientId={clientId}
          clientSecret={clientSecret}
          tokenEndpointAuthMethod={tokenEndpointAuthMethod}
          onClientIdChange={onClientIdChange}
          onClientSecretChange={onClientSecretChange}
          onTokenEndpointAuthMethodChange={onTokenEndpointAuthMethodChange}
        />
      );
  }

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        {availableTypes.length > 1 && (
          <>
            <Label className="text-muted-foreground text-xs">Client Type</Label>
            <Select
              value={clientType}
              onValueChange={(value) => onClientTypeChange(value as ClientType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {CLIENT_TYPE_LABELS[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <Text muted small>
          {clientTypeHelp(clientType, availableTypes)}
        </Text>
      </Stack>
      {credentials}
    </Stack>
  );
}
